import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Paper,
} from '@mui/material';
import {
  CollectionsRounded,
  SearchRounded,
  DeleteOutlineRounded,
  ContentCopyRounded,
  CheckRounded,
  RefreshRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  limit,
} from 'firebase/firestore';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { ImageUploader } from './media/ImageUploader';
import { formatFileSize } from './media/types';
import type { MediaImage } from './media/types';
import { formatDate } from '../../lib/i18n-format';
import { Trans, useTranslation } from 'react-i18next';

// ─── Page ─────────────────────────────────────────────────────────────────────

export function MediaLibraryPage() {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const tenantId = currentTenantId ?? '';

  const [images, setImages] = useState<MediaImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);

  // Delete confirmation dialog
  const [deleteTarget, setDeleteTarget] = useState<MediaImage | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ── Load images ────────────────────────────────────────────────────────────

  const loadImages = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, `tenants/${tenantId}/mediaLibrary`),
          orderBy('uploadedAt', 'desc'),
          limit(500),
        ),
      );
      setImages(
        snap.docs
          // Client-side: exclude soft-deleted docs (deletedAt present and non-null)
          .filter((d) => !d.data()['deletedAt'])
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              name: data['name'] ?? '',
              url: data['url'] ?? '',
              storagePath: data['storagePath'] ?? '',
              size: data['size'] ?? 0,
              mimeType: data['mimeType'] ?? '',
              uploadedAt: data['uploadedAt']?.toDate() ?? new Date(),
              uploadedBy: data['uploadedBy'] ?? '',
            } satisfies MediaImage;
          }),
      );
    } catch (e) {
      console.error('Error loading media library', e);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  // ── Filter ─────────────────────────────────────────────────────────────────

  const filtered = images.filter(
    (img) => search.trim() === '' || img.name.toLowerCase().includes(search.toLowerCase()),
  );

  // ── Copy URL ───────────────────────────────────────────────────────────────

  function copyUrl(img: MediaImage) {
    navigator.clipboard.writeText(img.url);
    setCopiedId(img.id);
    setSnack(t('crm.media.urlCopied'));
    setTimeout(() => setCopiedId(null), 2000);
  }

  // ── Soft delete ────────────────────────────────────────────────────────────

  async function confirmDelete() {
    if (!deleteTarget || !tenantId) return;
    setDeleting(true);
    try {
      // Delete from Storage — tolerate "object not found" (already gone or never uploaded)
      if (deleteTarget.storagePath) {
        try {
          await deleteObject(storageRef(storage, deleteTarget.storagePath));
        } catch (storageErr: any) {
          if (storageErr?.code !== 'storage/object-not-found') {
            throw storageErr; // Re-throw unexpected storage errors
          }
          // File already absent from Storage — proceed with Firestore cleanup anyway
          console.warn(
            'Storage object not found, proceeding with Firestore soft-delete:',
            deleteTarget.storagePath,
          );
        }
      }
      // Soft-delete the Firestore document
      await updateDoc(doc(db, `tenants/${tenantId}/mediaLibrary/${deleteTarget.id}`), {
        deletedAt: serverTimestamp(),
      });
      setImages((prev) => prev.filter((i) => i.id !== deleteTarget.id));
      setSnack(t('crm.media.deleted', { nombre: deleteTarget.name }));
    } catch (e: any) {
      setSnack(t('crm.media.deleteError', { motivo: e.message }));
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }

  // ── Handle new upload ──────────────────────────────────────────────────────

  function handleUploaded(img: MediaImage) {
    setImages((prev) => [img, ...prev]);
    setSnack(t('crm.media.uploaded', { nombre: img.name }));
  }

  // ── Stats ──────────────────────────────────────────────────────────────────

  const totalSize = images.reduce((sum, img) => sum + img.size, 0);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} mb={4}>
        <CollectionsRounded fontSize="large" color="primary" />
        <Box>
          <Typography variant="h4" fontWeight={700} color="white">
            {t('crm.media.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('crm.media.subtitle')}
          </Typography>
        </Box>
      </Stack>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="flex-start">
        {/* ── Left: uploader + stats ─────────────────────────────────────── */}
        <Box sx={{ width: { xs: '100%', lg: 280 }, flexShrink: 0 }}>
          <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} mb={2}>
                {t('crm.media.upload')}
              </Typography>
              {tenantId && <ImageUploader tenantId={tenantId} onUploaded={handleUploaded} />}
            </CardContent>
          </Card>

          {/* Stats */}
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              textTransform="uppercase"
              letterSpacing={1}
            >
              {t('crm.media.stats')}
            </Typography>
            <Stack spacing={1} mt={1.5}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t('crm.media.totalImages')}
                </Typography>
                <Chip label={images.length} size="small" />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t('crm.media.usedSpace')}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatFileSize(totalSize)}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Box>

        {/* ── Right: catalog ────────────────────────────────────────────── */}
        <Box flex={1} minWidth={0}>
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                <TextField
                  size="small"
                  placeholder={t('crm.media.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRounded fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1 }}
                />
                <Tooltip title={t('crm.media.refresh')}>
                  <IconButton
                    aria-label={t('crm.media.refresh')}
                    onClick={loadImages}
                    disabled={loading}
                  >
                    <RefreshRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              {loading ? (
                <ImageList cols={4} gap={8}>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <ImageListItem key={i}>
                      <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1.5 }} />
                    </ImageListItem>
                  ))}
                </ImageList>
              ) : filtered.length === 0 ? (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                  <CollectionsRounded sx={{ fontSize: 64, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">
                    {search ? t('crm.media.noneFound') : t('crm.media.empty')}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary" mb={1} display="block">
                    {/* PLURAL POR i18next, no con un ternario a mano: en español
                        e inglés hay dos formas, pero no en todos los idiomas, y
                        un ternario impone dos para siempre. */}
                    {t('crm.media.imageCount', { count: filtered.length })}
                    {search && t('crm.media.matching', { busqueda: search })}
                  </Typography>
                  <ImageList cols={4} gap={8} sx={{ m: 0 }}>
                    {filtered.map((img) => (
                      <ImageListItem
                        key={img.id}
                        sx={{
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <img
                          src={img.url}
                          alt={img.name}
                          loading="lazy"
                          style={{
                            width: '100%',
                            height: 120,
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                        <ImageListItemBar
                          title={
                            <Tooltip title={img.name}>
                              <span>{img.name}</span>
                            </Tooltip>
                          }
                          subtitle={
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <span>{formatFileSize(img.size)}</span>
                              <span>·</span>
                              <span>{formatDate(img.uploadedAt, { dateStyle: 'short' })}</span>
                            </Stack>
                          }
                          actionIcon={
                            <Stack direction="row">
                              <Tooltip title={t('crm.media.copyUrl')}>
                                <IconButton
                                  aria-label={t('crm.media.copyUrl')}
                                  size="small"
                                  sx={{ color: 'white' }}
                                  onClick={() => copyUrl(img)}
                                >
                                  {copiedId === img.id ? (
                                    <CheckRounded fontSize="small" />
                                  ) : (
                                    <ContentCopyRounded fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('crm.media.delete')}>
                                <IconButton
                                  aria-label={t('crm.media.delete')}
                                  size="small"
                                  sx={{ color: 'rgba(255,255,255,0.7)' }}
                                  onClick={() => setDeleteTarget(img)}
                                >
                                  <DeleteOutlineRounded fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          }
                          sx={{
                            '.MuiImageListItemBar-title': { fontSize: 11, fontWeight: 600 },
                            '.MuiImageListItemBar-subtitle': { fontSize: 10 },
                          }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      </Stack>

      {/* ── Delete confirm dialog ──────────────────────────────────────────── */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <WarningAmberRounded color="warning" />
            <Typography fontWeight={700}>{t('crm.media.deleteTitle')}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {/* EL NOMBRE VA DENTRO DE LA TRADUCCIÓN, no cosiendo dos mitades:
                en otro idioma puede caer en otra posición de la frase. */}
            <Trans
              i18nKey="crm.media.deleteAsk"
              ns="sis"
              values={{ nombre: deleteTarget?.name ?? '' }}
              components={{ b: <strong /> }}
            />
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            {t('crm.media.deleteHelpLong')}
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t('crm.media.cancel')}
          </Button>
          <Button
            onClick={confirmDelete}
            disabled={deleting}
            variant="contained"
            color="error"
            sx={{ borderRadius: 2 }}
          >
            {deleting ? t('crm.media.deleting') : t('crm.media.deleteShort')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ────────────────────────────────────────────────────────── */}
      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSnack(null)} sx={{ borderRadius: 2 }}>
          {snack}
        </Alert>
      </Snackbar>
    </Box>
  );
}
